import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import { INK_COMPATIBILITY_OPTIONS } from "../../utils/dummydata";
import { useNavigate } from "react-router-dom";
import { theme } from "../../utils/theme";

const YourLiveListing = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    if (!currentUser) return null;

    const {
        shopName,
        description,
        shopPhoto,
        address,
        type,
        selectedSupplies,
        inkTypes,
    } = currentUser;

    const fullAddress = address
        ? `${address.city || ""}, ${address.province || ""}`.replace(", ,", ",")
        : "No location set";

    const t = theme[type];

    const visibleTags = selectedSupplies?.slice(0, 4) || [];
    const extraCount = (selectedSupplies?.length || 0) - visibleTags.length;

    const INK_MAP = Object.fromEntries(
        INK_COMPATIBILITY_OPTIONS.map((item) => [item.key, item])
    );

    return (
        <div className="bg-white rounded-[16px] border border-[#e4e5e7] overflow-hidden mb-[24px]">

            {/* HEADER */}
            <div className="flex items-center justify-between px-[20px] py-[14px] border-b border-[#f0f1f3]">

                <div className="flex items-center gap-[8px]">
                    <div className={`w-[8px] h-[8px] rounded-full bg-[${t.primary}]`} />
                    <p className="font-semibold text-[15px] text-[#212326]">
                        Your Live Listing
                    </p>
                </div>

                <button
                    onClick={() => navigate("/marketplace")}
                    className={`flex items-center gap-[4px] font-semibold text-[13px] text-[${t.primary}] hover:underline`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        className="w-[12px] h-[12px]">
                        <path d="M15 3h6v6"></path>
                        <path d="M10 14 21 3"></path>
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    </svg>
                    View on Marketplace
                </button>
            </div>

            {/* BODY */}
            <div className="p-[20px]">
                <div className="flex flex-col md:flex-row gap-[20px]">

                    {/* IMAGE */}
                    <div className="w-full md:w-[200px] h-[140px] rounded-[12px] bg-[#f0fdf8] flex items-center justify-center shrink-0 overflow-hidden">
                        {shopPhoto ? (
                            <img
                                src={shopPhoto}
                                alt={shopName}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                className="w-[40px] h-[40px] text-[#4a6cf7]">
                                <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                                <path d="M12 22V12"></path>
                                <polyline points="3.29 7 12 12 20.71 7"></polyline>
                                <path d="m7.5 4.27 9 5.15"></path>
                            </svg>
                        )}
                    </div>

                    {/* DETAILS */}
                    <div className="flex-1">

                        {/* TITLE + BADGE */}
                        <div className="flex items-start justify-between mb-[8px]">

                            <div>
                                <h3 className="font-semibold text-[18px] text-[#212326] mb-[2px]">
                                    {shopName || "Your Shop"}
                                </h3>

                                <div className="flex items-center gap-[6px] text-[#717182]">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                        className="w-[13px] h-[13px]">
                                        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                                        <circle cx="12" cy="10" r="3"></circle>
                                    </svg>

                                    <span className="font-medium text-[13px]">
                                        {fullAddress}
                                    </span>
                                </div>
                            </div>

                            <div className={`flex items-center gap-[4px] bg-[${t.bg}] rounded-[8px] px-[8px] py-[4px]`}>
                                <div className={`flex items-center gap-[4px] bg-[${t.bg}] rounded-[8px] px-[4px] py-[2px]`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                        class={`lucide lucide-package w-[12px] h-[12px] text-[${t.primary}]`}>
                                        <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                                        <path d="M12 22V12"></path>
                                        <polyline points="3.29 7 12 12 20.71 7"></polyline>
                                        <path d="m7.5 4.27 9 5.15"></path>
                                    </svg>
                                    <span class={`font-semibold text-[11px] text-[${t.primary}] capitalize`}>{type || "Shop"}</span>
                                </div>
                            </div>
                        </div>

                        {/* DESCRIPTION */}
                        <p className="font-medium text-[13px] text-[#717182] leading-[1.5] mb-[12px]">
                            {description || "No description added yet."}
                        </p>

                        {/* SERVICES */}
                        <div className="flex flex-wrap gap-[6px] mb-[10px]">
                            {visibleTags?.map((item, index) => (
                                <span
                                    key={index}
                                    className="bg-[#f4f5f6] px-[10px] py-[4px] rounded-[8px] font-medium text-[12px] text-[#53585f]"
                                >
                                    {item}
                                </span>
                            ))}

                            {extraCount > 0 && (
                                <span className="bg-[#f4f5f6] px-[10px] py-[4px] rounded-[8px] font-medium text-[12px] text-[#9298a0]">
                                    +{extraCount} more
                                </span>
                            )}
                        </div>

                        {/* INK TYPES */}
                        {inkTypes?.length > 0 && (
                            <div className="flex items-center gap-[6px] flex-wrap">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                    className={`w-[12px] h-[12px] text-[${t.primary}]`}>
                                    <path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"></path>
                                    <path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"></path>
                                </svg>

                                {inkTypes.slice(0, 3).map((key) => {
                                    const ink = INK_MAP[key];

                                    if (!ink) return null;

                                    return (
                                        <span
                                            key={key}
                                            className={`bg-[${t.bg}] text-[${t.primary}] rounded-[6px] font-semibold px-[7px] py-[2px] text-[10px]`}
                                        >
                                            {ink.title}
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default YourLiveListing;